import type { CustomerConsultationSummary } from '@/stores/customerConsultationsHub'
import { CLINICAL_CONSULT_DURATION_MINUTES } from '@/stores/clinicalConsultationBookingFlow'

const PAST_UI_STATUSES = new Set(['pending', 'completed', 'no_show', 'cancelled', 'payment_due'])

/** Featured hub rows that need live Firestore updates (matches customerConsultationsHub store). */
const FEATURED_UI_STATUSES_FOR_WATCH = ['upcoming', 'resume'] as const

function hubVisitEndMs(summary: CustomerConsultationSummary): number | null {
  if (!summary.scheduledStart) {
    return null
  }
  const startMs = new Date(summary.scheduledStart).getTime()
  if (summary.scheduledEnd) {
    return new Date(summary.scheduledEnd).getTime()
  }
  const durationMinutes = CLINICAL_CONSULT_DURATION_MINUTES[summary.consultType] ?? 30
  return startMs + durationMinutes * 60_000
}

/** PAD-HUB: visit window under Upcoming with In progress chip. */
export function isHubInProgressAppointment(
  summary: CustomerConsultationSummary,
  now: Date = new Date(),
): boolean {
  if (summary.uiStatus !== 'upcoming' || !summary.scheduledStart) {
    return false
  }
  const startMs = new Date(summary.scheduledStart).getTime()
  if (now.getTime() < startMs) {
    return false
  }
  const endMs = hubVisitEndMs(summary)
  return endMs !== null && now.getTime() < endMs
}

/** PAD-HUB: after scheduledEnd, presentation moves to Past as Pending until server snapshot catches up. */
export function isHubEndedAwaitingResolve(
  summary: CustomerConsultationSummary,
  now: Date = new Date(),
): boolean {
  if (summary.uiStatus !== 'upcoming') {
    return false
  }
  const endMs = hubVisitEndMs(summary)
  return endMs !== null && now.getTime() >= endMs
}

export function withHubPresentationUiStatus(
  summary: CustomerConsultationSummary,
  now: Date = new Date(),
): CustomerConsultationSummary {
  if (isHubEndedAwaitingResolve(summary, now)) {
    return { ...summary, uiStatus: 'pending' }
  }
  return summary
}

export function pickInProgressAppointmentSummaries(
  summaries: CustomerConsultationSummary[],
  excludeConsultationId?: string | null,
  now: Date = new Date(),
): CustomerConsultationSummary[] {
  return summaries
    .filter(
      (row) => isHubInProgressAppointment(row, now) && row.consultationId !== excludeConsultationId,
    )
    .sort((a, b) => {
      const aTime = a.scheduledStart ? new Date(a.scheduledStart).getTime() : 0
      const bTime = b.scheduledStart ? new Date(b.scheduledStart).getTime() : 0
      return bTime - aTime
    })
}

export function pickInProgressAppointmentSummary(
  summaries: CustomerConsultationSummary[],
  activeClinicalConsultationId?: string | null,
  now: Date = new Date(),
): CustomerConsultationSummary | undefined {
  const current = summaries.filter((row) => isHubInProgressAppointment(row, now))
  if (current.length === 0) {
    return undefined
  }
  if (current.length === 1) {
    return current[0]
  }

  if (activeClinicalConsultationId) {
    const activeMatch = current.find((row) => row.consultationId === activeClinicalConsultationId)
    if (activeMatch) {
      return activeMatch
    }
  }

  const withScheduledStart = current.filter((row) => row.scheduledStart)
  if (withScheduledStart.length === 0) {
    return current[0]
  }

  return withScheduledStart.reduce((earliest, row) => {
    const rowTime = new Date(row.scheduledStart!).getTime()
    const earliestTime = new Date(earliest.scheduledStart!).getTime()
    return rowTime < earliestTime ? row : earliest
  })
}

/** PAD-ONE: one featured upcoming when multiple exist (e.g. cancel + rebook overlap). */
export function pickUpcomingSummary(
  summaries: CustomerConsultationSummary[],
  activeClinicalConsultationId?: string | null,
): CustomerConsultationSummary | undefined {
  const upcoming = summaries.filter((row) => row.uiStatus === 'upcoming')
  if (upcoming.length === 0) {
    return undefined
  }
  if (upcoming.length === 1) {
    return upcoming[0]
  }

  if (activeClinicalConsultationId) {
    const activeMatch = upcoming.find((row) => row.consultationId === activeClinicalConsultationId)
    if (activeMatch) {
      return activeMatch
    }
  }

  const withScheduledStart = upcoming.filter((row) => row.scheduledStart)
  if (withScheduledStart.length === 0) {
    return upcoming[0]
  }

  return withScheduledStart.reduce((latest, row) => {
    const rowTime = new Date(row.scheduledStart!).getTime()
    const latestTime = new Date(latest.scheduledStart!).getTime()
    return rowTime > latestTime ? row : latest
  })
}

export function pickFutureUpcomingSummary(
  summaries: CustomerConsultationSummary[],
  activeClinicalConsultationId?: string | null,
  now: Date = new Date(),
): CustomerConsultationSummary | undefined {
  const futureUpcoming = summaries.filter(
    (row) =>
      row.uiStatus === 'upcoming' &&
      !isHubInProgressAppointment(row, now) &&
      !isHubEndedAwaitingResolve(row, now),
  )
  return pickUpcomingSummary(futureUpcoming, activeClinicalConsultationId)
}

export function resolveConsultationHubWatchConsultationIds(args: {
  summaries: CustomerConsultationSummary[]
  activeClinicalConsultationId?: string | null
  now?: Date
}): string[] {
  const now = args.now ?? new Date()
  const ids: string[] = []
  const future = pickFutureUpcomingSummary(args.summaries, args.activeClinicalConsultationId, now)
  const inProgress = pickInProgressAppointmentSummary(
    args.summaries,
    args.activeClinicalConsultationId,
    now,
  )
  if (future) {
    ids.push(future.consultationId)
  }
  if (inProgress && inProgress.consultationId !== future?.consultationId) {
    ids.push(inProgress.consultationId)
  }
  if (ids.length > 0) {
    return ids
  }

  for (const status of FEATURED_UI_STATUSES_FOR_WATCH) {
    if (status === 'upcoming') {
      continue
    }

    const featured = args.summaries.find((row) => row.uiStatus === status)
    if (featured) {
      return [featured.consultationId]
    }
  }

  if (args.activeClinicalConsultationId) {
    return [args.activeClinicalConsultationId]
  }

  const pastSummaries = args.summaries.filter((row) => PAST_UI_STATUSES.has(row.uiStatus))
  if (pastSummaries.length === 0) {
    return []
  }

  const withScheduledStart = pastSummaries.filter((row) => row.scheduledStart)
  if (withScheduledStart.length === 0) {
    const id = pastSummaries[0]?.consultationId
    return id ? [id] : []
  }

  const latestPast = withScheduledStart.reduce((latest, row) => {
    const rowTime = new Date(row.scheduledStart!).getTime()
    const latestTime = new Date(latest.scheduledStart!).getTime()
    return rowTime > latestTime ? row : latest
  })
  return [latestPast.consultationId]
}

export function resolveConsultationHubWatchId(args: {
  summaries: CustomerConsultationSummary[]
  activeClinicalConsultationId?: string | null
  now?: Date
}): string | null {
  const ids = resolveConsultationHubWatchConsultationIds(args)
  return ids[0] ?? null
}
